import React from 'react';
import { Link } from 'react-router-dom';
import { Home } from 'lucide-react';

const PricingSection = () => {
  const plans = [
    {
      name: 'DIY Starter',
      price: 29,
      description: 'Perfect for getting started with AI marketing',
      popular: false,
      features: [
        'Full Marketing Studio access',
        '120-day SMART goal planning',
        'AI strategy generation',
        'Content calendar & roadmap',
        'Performance analytics',
        'Email support (48h)',
      ],
      cta: 'Get Started',
      ctaLink: '/auth',
      showTrial: true,
    },
    {
      name: 'Assisted Growth',
      price: 129,
      description: 'For businesses ready to grow with automation',
      popular: true,
      features: [
        'Everything in DIY Starter',
        'CRM (up to 500 customers)',
        '8 AI-generated posts/month',
        'Instagram, Facebook, LinkedIn',
        '1 strategy call/month (30 min)',
        'Customer activity tracking',
        'Advanced analytics',
        'Priority email support (24h)',
      ],
      cta: 'Get Started',
      ctaLink: '/auth',
      showTrial: false,
    },
    {
      name: 'Fully Managed VIP',
      price: 449,
      description: 'Complete hands-off marketing solution',
      popular: false,
      features: [
        'Everything in Assisted Growth',
        'Unlimited CRM customers',
        '16 AI posts + 4 custom posts/month',
        '2 strategy calls/month (60 min)',
        'Dedicated account manager',
        'Local SEO optimization',
        'Google My Business management',
        'Priority support (same day)',
      ],
      cta: 'Book a Demo',
      ctaLink: '/auth',
      showTrial: false,
    },
  ];

  return (
    <>
      {/* Back to Home Button */}
      <div className="bg-black border-b border-zinc-800">
        <div className="max-w-[1100px] mx-auto px-5 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-400 hover:text-white transition-colors duration-300 hover:gap-3"
          >
            <Home size={18} />
            <span>Back to Home</span>
          </Link>
        </div>
      </div>

      <section id="pricing" className="py-20 px-5 bg-black">
        <div className="max-w-[1100px] mx-auto">

        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Simple, <span className="text-blue-500">Transparent Pricing</span>
          </h2>
          <p className="text-lg text-gray-400 mb-6">
            Start free, upgrade as you grow. No hidden fees, cancel anytime.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {plans.map((plan, idx) => (
            <div
              key={idx}
              className={`relative bg-[#0a0a0a] rounded-xl border-2 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_15px_30px_rgba(59,130,246,0.12)] ${
                plan.popular
                  ? 'border-blue-500 shadow-[0_15px_30px_rgba(59,130,246,0.25)]'
                  : 'border-[#1f1f1f] hover:border-blue-500'
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-[0_4px_12px_rgba(59,130,246,0.5)] whitespace-nowrap">
                  Most Popular
                </div>
              )}

              {/* Card Content */}
              <div className="p-8">
                {/* Plan Name & Description */}
                <h3 className="text-2xl font-bold text-white mb-2">
                  {plan.name}
                </h3>
                <p className="text-sm text-gray-400 mb-6">
                  {plan.description}
                </p>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline mb-1">
                    <span className="text-xl font-semibold text-gray-400">£</span>
                    <span className="text-5xl font-extrabold text-white ml-1">
                      {plan.price}
                    </span>
                    <span className="text-base text-gray-400 ml-2">/month</span>
                  </div>
                </div>

                {/* Features List */}
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, featureIdx) => (
                    <li key={featureIdx} className="text-sm text-gray-200 flex items-start">
                      <span className="text-blue-500 mr-2 flex-shrink-0">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Link
                  to={plan.ctaLink}
                  className={`block w-full py-3 px-4 text-center font-semibold text-sm rounded-lg transition-all duration-300 no-underline ${
                    plan.popular
                      ? 'bg-blue-500 text-white hover:bg-blue-600 hover:-translate-y-1 hover:shadow-[0_6px_16px_rgba(59,130,246,0.4)]'
                      : 'bg-[#1f1f1f] text-white border border-[#2a2a2a] hover:bg-[#2a2a2a] hover:-translate-y-1 hover:shadow-[0_6px_16px_rgba(255,255,255,0.1)]'
                  }`}
                >
                  {plan.cta}
                </Link>

                {/* Trial Note */}
                {plan.showTrial && (
                  <p className="text-center text-xs text-gray-500 mt-3">
                    30-day free trial • No credit card required
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Trust Signals */}
        <div className="flex flex-wrap justify-center items-center gap-8 p-8 bg-[#0a0a0a] rounded-xl border border-[#1f1f1f] shadow-[0_2px_8px_rgba(255,255,255,0.05)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#1a2332] rounded-full flex items-center justify-center text-sm">
              ✓
            </div>
            <span className="text-sm text-gray-400">30-day money-back guarantee</span>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#1a2332] rounded-full flex items-center justify-center text-sm">
              🔒
            </div>
            <span className="text-sm text-gray-400">Secure payment with Stripe</span>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#1a2332] rounded-full flex items-center justify-center text-sm">
              ↻
            </div>
            <span className="text-sm text-gray-400">Cancel anytime</span>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default PricingSection;